// Two tabs, one device (user-found, Sep 2026: "I created a playlist, switched
// tab, and the playlist has gone"). Every tab keeps the store in memory and
// the persist middleware writes the tab's WHOLE slice to localStorage on any
// change, so a tab holding an older copy — woken by nothing more than the
// account sync on returning to view — wrote its copy over a playlist the
// other tab had just made.
//
// The browser tells every OTHER tab when localStorage changes (the `storage`
// event). On each one this adopts, key by key, the shared documents
// (CROSS_TAB_KEYS: saved tunes, playlists, per-tune memory…) whose change
// stamp is newer than this tab's, so this tab's next write carries them too.
// Per key, so two tabs changing different things both keep their change; by
// stamp, so a stale write never wins and an adoption's own write (which
// carries the same stamps back) ends the exchange instead of echoing it.
//
// What stays each tab's own: the tune on screen, the tempo, the view — two
// tabs can study two tunes. A write from a different persist version (a tab
// left open across a deploy) is ignored rather than guessed at.

import { asForeignWrite } from '@/data/accountSync';
import { crossTabPatch, useStore } from './store';

/** Adopt what another tab wrote, if anything in it is newer. True when it did. */
export function adoptOtherTabWrite(newValue: string | null): boolean {
  if (!newValue) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(newValue);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== 'object') return false;
  const { state, version } = parsed as { state?: unknown; version?: unknown };
  if (version !== useStore.persist.getOptions().version) return false;
  if (!state || typeof state !== 'object') return false;
  const patch = crossTabPatch(state as Record<string, unknown>, useStore.getState());
  if (!patch) return false;
  asForeignWrite(() => useStore.setState(patch));
  return true;
}

/** Listen for the other tabs' writes. Returns the cleanup. */
export function startCrossTabSync(): () => void {
  const name = useStore.persist.getOptions().name;
  const onStorage = (event: StorageEvent) => {
    if (event.storageArea !== window.localStorage || event.key !== name) return;
    adoptOtherTabWrite(event.newValue);
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
